import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Mail, Paperclip } from 'lucide-react';

/**
 * Generic "Send by email" dialog — works for both invoices and proposals.
 *
 * @param {string}      defaultTo          pre-filled recipient email
 * @param {string}      defaultSubject     pre-filled subject line
 * @param {ReactNode}   bodyPreview        compact preview rendered inside the dialog
 * @param {boolean}     showAttachPdf      show the "Attach PDF" checkbox
 * @param {boolean}     defaultAttachPdf   default state of the checkbox
 * @param {function}    onSend             async (to, subject, message, attachPdf) => void
 * @param {boolean}     sending            external loading state
 */
export default function SendEmailDialog({
    open, onClose,
    defaultTo = '', defaultSubject = '',
    bodyPreview = null,
    showAttachPdf = false, defaultAttachPdf = true,
    onSend, sending = false,
}) {
    const [to, setTo] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [attachPdf, setAttachPdf] = useState(defaultAttachPdf);

    useEffect(() => {
        if (open) {
            setTo(defaultTo);
            setSubject(defaultSubject);
            setMessage('');
            setAttachPdf(defaultAttachPdf);
        }
    }, [open, defaultTo, defaultSubject, defaultAttachPdf]);

    const submit = async (e) => {
        e.preventDefault();
        if (!to.trim()) return;
        await onSend(to.trim(), subject.trim(), message.trim(), showAttachPdf ? attachPdf : false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && !sending && onClose()}>
            <DialogContent className="max-w-[520px]" data-testid="send-email-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-[19px] flex items-center gap-2">
                        <Mail size={17} className="text-[color:var(--bm-primary)]" /> Send by email
                    </DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">
                        Sent via your connected Microsoft account. Saves to Sent Items.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-4 mt-1">
                    <div>
                        <Label className="text-[12.5px] font-medium">To *</Label>
                        <Input value={to} onChange={e => setTo(e.target.value)}
                               placeholder="client@example.com"
                               className="mt-1.5 h-9" required
                               data-testid="send-email-to" />
                    </div>
                    <div>
                        <Label className="text-[12.5px] font-medium">Subject</Label>
                        <Input value={subject} onChange={e => setSubject(e.target.value)}
                               className="mt-1.5 h-9"
                               data-testid="send-email-subject" />
                    </div>

                    {bodyPreview && (
                        <div>
                            <Label className="text-[12.5px] font-medium text-[color:var(--bm-text-muted)]">What's included</Label>
                            <div className="mt-1.5 rounded-md border border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)] px-4 py-3 text-[12.5px] text-[color:var(--bm-text-muted)] space-y-1.5">
                                {bodyPreview}
                            </div>
                        </div>
                    )}

                    <div>
                        <Label className="text-[12.5px] font-medium text-[color:var(--bm-text-muted)]">
                            Personal note <span className="font-normal">(optional)</span>
                        </Label>
                        <Textarea value={message} onChange={e => setMessage(e.target.value)}
                                  placeholder="Add a personal message appended to the email…"
                                  rows={2} className="mt-1.5"
                                  data-testid="send-email-message" />
                    </div>

                    {showAttachPdf && (
                        <label className="flex items-center gap-2.5 text-[13px] cursor-pointer select-none">
                            <input type="checkbox" checked={attachPdf} onChange={e => setAttachPdf(e.target.checked)}
                                   className="cursor-pointer" data-testid="send-email-attach-pdf" />
                            <Paperclip size={13} className="text-[color:var(--bm-text-subtle)]" />
                            Attach PDF
                        </label>
                    )}

                    <DialogFooter className="pt-2">
                        <Button type="button" variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
                        <Button type="submit" disabled={sending || !to.trim()}
                                style={{ background: 'var(--bm-primary)' }}
                                data-testid="send-email-submit-btn">
                            <Mail size={14} className="mr-1.5" />
                            {sending ? 'Sending…' : 'Send email'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
